<script lang="ts" setup>
import type { SubjectViewProps } from '~/models/Subject'
import type { ProductHeaders } from '~/models/ProductHeaders'
import type { SectionBaseItem } from '~/models/Content/SectionBaseItem'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import arrayUtils from '~/utils/arrayUtils'
import { useSubjectViewStore } from '~/stores/subjectView'
import useSubjectsStore from '~/stores/subjects'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import useSectionPath from '~/composables/useSectionPath'
import useQueryFilter from '~/composables/useQueryFilter'
import useContentApi from '~/api/contentApi'
import SectionsSkeleton from '~/components/skeletons/SectionsSkeleton.vue'
import TeacherResources from '~/components/ResourceFinder/TeacherResources.vue'
import ResourceFinder from '~/components/ResourceFinder/ResourceFinder.vue'
import SectionRenderer from '~/components/ResourceEngine/SectionRenderer.vue'
import SubjectBanner from '~/components/banners/SubjectBanner.vue'

const props = defineProps<SubjectViewProps>()

const { t } = useI18n()
const filterStore = useFilterStore()
const { filterWatchers, $reset } = filterStore
const { selectedGrade, selectedHeaders, isLoading: filtersLoading } = storeToRefs(filterStore)

const { setCurrentSubject } = useSubjectsStore()
const { isTeacher } = storeToRefs(useAuthStore())
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())
const { subjectPath } = useSectionPath()
const { findContents } = useContentApi()
const { intersect, empty } = arrayUtils()

const { loadSubjectData } = useSubjectViewStore()
const { subjectHeader } = storeToRefs(useSubjectViewStore())

const { data: sections, isLoading: sectionsLoading } = useQuery({
  enabled: computed(() => !!subjectPath[props.subjectCode]),
  queryKey: computed(() => ['legacy_sections', props.subjectCode, selectedGrade.value]),
  queryFn: () => findContents<SectionBaseItem>({
    subtreeCriterion: [subjectPath[props.subjectCode]!],
    sortField: 'priority',
    sortOrder: 'asc',
    contentTypeCriterion: [
      ContentType.SectionBox,
      ContentType.SectionExpanded,
      ContentType.SectionStandalone,
    ]
  }),
  select: sections => sections.filter(({ grades, subjects }) => {
    const hasGrades = empty(grades) || !empty(intersect(grades, [selectedGrade.value]))
    const hasSubjects = empty(subjects) || !empty(intersect(subjects, [props.subjectCode]))
    return hasGrades && hasSubjects
  }),
  staleTime: Infinity,
})

const watchers = filterWatchers()

onMounted(() => {
  useQueryFilter()
  setCurrentSubject(props.subjectCode)
})

onBeforeUnmount(() => {
  watchers.stopFilterWatchers()
  setCurrentSubject(undefined)
  $reset()
})

watch(props, async () => {
  setCurrentSubject(props.subjectCode)
})

watch(subjectHeader, (newHeader) => {
  setTitle(t(newHeader.title))
}, { deep: true })

watch(props, () => {
  if (!subjectPath[props.subjectCode])
    return
  loadSubjectData(props.subjectCode)
    .catch(error => console.warn(error))
}, { immediate: true })

const headerKey = computed((): keyof ProductHeaders => props.isArchive
  ? 'archivedContent'
  : 'learningPath'
)

const showSections = computed(() => !sectionsLoading.value
  && !filtersLoading.value
  && !!sections?.value?.length
  && !selectedHeaders.value.length)

const showTeacherResources = computed(() => isTeacher.value
  && hasLoadedProducts.value
  && !selectedHeaders.value.length
  && selectedGrade.value
  && props.subjectCode)
</script>

<template>
  <SubjectBanner
    :title="t(subjectHeader.title)"
    :image="subjectHeader.image"
    :text="subjectHeader.text"
  />

  <ResourceFinder :header-key />

  <TeacherResources
    v-if="showTeacherResources"
    :subject-code="subjectCode"
    :grade-code="selectedGrade"
  />

  <SectionRenderer
    v-if="showSections && sections"
    :items="sections"
  />
  <div
    v-if="sectionsLoading"
    class="max-w-screen-au mx-auto"
  >
    <SectionsSkeleton />
  </div>
</template>
